import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState, useSyncExternalStore, type DragEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { isEffectivelyLocked, type SceneStore } from '@os/doc';
import type { Point } from '@os/engine';
import type { ImportFailure } from '../media/import';
import { usePageNav } from '../pages/usePageNav';
import type { PageUrl } from '../pages/page-nav';
import { EditorController, type CommentRequest, type ContextMenuRequest, type Notice } from './controller';
import type { MediaResolver } from './lod';
import { ArrangePanel } from './ArrangePanel';
import { BoardSettings } from './BoardSettings';
import { useEditorGestureGuard } from './gesture-guard';
import { readStoredSnap, writeSnapPref } from './snap-pref';
import { Minimap, useMinimapOpen } from './Minimap';
import type { MainMenuHome } from './MainMenu';
import { ContextMenu } from './ContextMenu';
import { menuItemsFor } from './menu-items';
import { lastCopied } from '../clipboard/clipboard';
import { Overlay } from './Overlay';
import { PropertiesPanel } from './PropertiesPanel';
import { ContextBar } from './ContextBar';
import { QuickCreateMenu } from './QuickCreateMenu';
import { Button, FilePicker } from '../../ui';
import { Toast } from '../../ui/Toast';
import { SectionTitleEditor } from '../objects/SectionTitleEditor';
import { ConnectorLabelEditor } from '../objects/ConnectorLabelEditor';
import { requestCellEdit } from '../table/cell-edit';
import { requestTaskEdit } from '../tasks/task-edit';
import { ShortcutsHelp } from './ShortcutsHelp';
import { Toolbar } from './Toolbar';
import { PresenceLayer, type PresenceProps } from '../presence/PresenceLayer';
import { World } from './World';
import { useUnfurl } from '../links/useUnfurl';
import { useClipboard } from '../clipboard/useClipboard';
import type { LinkServices } from '../links/services';
import { RichTextEditor } from '../text/RichTextEditor';
import { CommentsLayer, type CommentsProps } from '../comments/CommentsLayer';
import { ExportDialog, type ExportFormat, type ExportSetup } from '../export/ExportDialog';
import { sectionsInReadingOrder, type ExportTarget } from '../export/plan';
import { ZoomControls } from './ZoomControls';
import { CommandPalette } from '../palette/CommandPalette';
import { usePaletteCommands, usePaletteShortcut, useRegisteredCommands } from '../palette/registry';
import { PagesPanel } from '../pages/PagesPanel';
import { PageSwitcher } from '../pages/PageSwitcher';
import { MoveToPageDialog } from '../pages/MoveToPageDialog';
import { pageCommands } from '../pages/page-commands';
import { pageLabel } from '../pages/labels';
import { PageLinkDialog } from '../pages/PageLinkDialog';
import { PAGE_LINK_SIZE } from '../pages/miniature';
import { editorCommands, menuCommands, uniqueById } from './palette-commands';
import { keyboardInset } from './keyboard-inset';
import { isCoarsePointer, useLayout } from '../../app/layout';
import { Announcer } from './Announcer';
import { InTopBar } from './TopBar';
import { MainMenu } from './MainMenu';
import { AboutDialog } from '../about/AboutDialog';
import { mainMenu } from './main-menu';
import { CsvImportDialog } from '../import/csv/CsvImportDialog';
import { FigmaImportDialog } from '../import/figma/FigmaImportDialog';
import type { Api } from '../../lib/api';
import { writeThemePreference } from '../../app/theme-store';
import { BoardLimitNotice } from './BoardLimitNotice';
import { AuthorTag } from './AuthorTag';
import type { AuthorsSource } from './authors';
import { CanvasAuthorsProvider } from './CanvasAuthors';

/** `adopt`: called on the created images inside their undo step. */
export type ImportHandler = (files: File[], at: Point, adopt?: (ids: string[]) => void) => Promise<{ failed: ImportFailure[] }>;
/** One picture imported as an asset, placed nowhere ("Replace image", P5f). */
export type AssetImportHandler = (file: File) => Promise<{ asset: { assetId: string; w: number; h: number } | null; failed: ImportFailure[] }>;

type Props = {
  store: SceneStore;
  media: MediaResolver;
  /** Picker, paste and drop all end here; absent means the board cannot take images. */
  onImport?: ImportHandler;
  onImportAsset?: AssetImportHandler;
  /** Injected by tests (clock, viewport size); created on mount otherwise. */
  controller?: EditorController;
  onObjectRender?: (id: string) => void;
  /** Live boards: peers' avatars, cursors and selections. */
  presence?: PresenceProps | null;
  /** Server boards: link previews (local boards keep the bare URL). */
  links?: LinkServices | null;
  /** False without `can_export`: copy and cut leave the clipboard alone. */
  canCopyOut?: boolean;
  /** Server boards: comment pins, threads and the comment tool (P7). */
  comments?: CommentsProps | null;
  /** Export entry points (toolbar, context menu); null or absent without `can_export` (spec §9.3). */
  exporter?: ExportSetup | null;
  /** "Import a file here" (P9): a `.osboard` or `BoardFile` JSON added at `at` like a paste. */
  importFile?: (file: File, at: Point) => Promise<{ ids: string[]; skipped: number; missing: number }>;
  /** Server boards, members (P11b): File › Import › From Figma through the Worker; `ingest` stores an image as a board asset. */
  importFigma?: { api: Pick<Api, 'figmaFile' | 'figmaImage'>; boardId: string; ingest(blob: Blob, name: string, sha256: string | null): Promise<string | null> };
  /** Server boards: who added each object (P5d); local boards have none. */
  authors?: AuthorsSource | null;
  /** Where the brand mark leads (P16): absent = a plain mark (guests by link). */
  home?: MainMenuHome;
  /** Remembers this person's snap-to-grid choice for the board (P16); absent, it lasts until the editor closes. */
  boardId?: string;
  /** Where the active page is written (P14); null for a board shown inside another (a version preview). */
  pageUrl?: PageUrl | null;
};

const imagesOf = (list: Iterable<File> | null | undefined) => [...(list ?? [])].filter((f) => f.type.startsWith('image/') || f.type === '');

/**
 * Full-size canvas: world layer (DOM objects) + screen overlay (SVG) inside the viewport, which owns the only
 * pointer handler; toolbar, properties and dialogs are siblings so their events never reach the canvas tools.
 */
/** Switching board page (P15c): the world fades in for this long (--duration-fast, opacity only; panning stays instant). */
export const PAGE_ENTER_MS = 160;

export function Editor({ store, media, onImport, onImportAsset, controller: injected, onObjectRender, presence, links, canCopyOut = true, comments, exporter, importFile, importFigma, authors, home, boardId, pageUrl }: Props) {
  const { t } = useTranslation();
  const [controller] = useState(() => injected ?? new EditorController({ store, media, browseFirst: isCoarsePointer() }));
  // Pages (P14): `?page=`, the camera of each page, the stack of « ← Page précédente ».
  const nav = usePageNav(store, controller, boardId, pageUrl);
  /** A page deleted in the pages panel (counts up so a second deletion restarts the toast); 0 = no toast. */
  const [pageDeleted, setPageDeleted] = useState(0);
  // Another page shown: mark the root for PAGE_ENTER_MS (the world fades in, CSS). Straight on the DOM, so the editor
  // does not re-render for it (re-rendering it here cost frames while menus animated, E2E p5g-motion).
  const root = useRef<HTMLDivElement>(null);
  useEffect(() => {
    let shown = store.page;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const off = store.subscribePage(() => {
      if (store.page === shown) return;
      shown = store.page;
      root.current?.setAttribute('data-page-enter', '');
      clearTimeout(timer);
      timer = setTimeout(() => root.current?.removeAttribute('data-page-enter'), PAGE_ENTER_MS);
    });
    return () => {
      off();
      clearTimeout(timer);
    };
  }, [store]);
  const [pagesOpen, setPagesOpen] = useState(false);
  const [moving, setMoving] = useState<string[] | null>(null);
  /** The page-link dialog: a new link at the view's centre, or `retarget` an existing one (P14). */
  const [linking, setLinking] = useState<{ retarget?: string } | null>(null);
  // A table as a CSV file (P11b Ruling 4), named after the board.
  const exportTableCsv = (id: string) => {
    const o = store.getObject(id);
    if (o?.type === 'table') void import('../table/export-csv').then((m) => m.downloadTableCsv(o, exporter?.title ?? ''));
  };
  // File › Import › CSV (P11b): where the import lands (the view's centre when it opened).
  const [csvAt, setCsvAt] = useState<Point | null>(null);
  const [figmaAt, setFigmaAt] = useState<Point | null>(null);
  const openPageLink = (id: string) => {
    const o = store.getObject(id);
    if (o?.type === 'pagelink' && store.pages().some((p) => p.id === o.targetPageId)) nav.go(o.targetPageId, { push: true });
  };
  const pickLinkTarget = (pageId: string) => {
    const retarget = linking?.retarget;
    setLinking(null);
    if (retarget) store.commands.setProps([retarget], { targetPageId: pageId });
    else {
      const c = controller.viewCenter();
      const { w, h } = PAGE_LINK_SIZE;
      const id = store.commands.createObject({ type: 'pagelink', targetPageId: pageId, parentId: null, x: c.x - w / 2, y: c.y - h / 2, w, h, rotation: 0, locked: false, style: {} });
      if (id) controller.setSelection([id]);
    }
    controller.focus();
  };
  // ⌘K and the phone's Board menu (P14).
  // A preview (`pageUrl` null) adds nothing to the board's palette: its pages are the version's, not the board's.
  usePaletteCommands(() => (pageUrl === null ? [] : [
    ...pageCommands({ store, nav, t, openPanel: () => setPagesOpen(true) }),
    ...(store.readOnly ? [] : [{ id: 'pageLink', label: t('pages.linkCreate'), group: 'board' as const, keywords: ['lien', 'link', 'page', 'miniature'], run: () => setLinking({}) }]),
    ...(store.readOnly || !importFigma ? [] : [{ id: 'importFigma', label: t('palette.importFigma'), group: 'board' as const, keywords: ['figma', 'figjam', 'import', 'importer'], run: () => setFigmaAt(controller.viewCenter()) }]),
    ...(store.readOnly ? [] : [{ id: 'importCsv', label: t('palette.importCsv'), group: 'board' as const, keywords: ['csv', 'figjam', 'import', 'importer', 'tableur', 'spreadsheet'], run: () => setCsvAt(controller.viewCenter()) }]),
  ]));
  const phone = useLayout() === 'phone';
  const [helpOpen, setHelpOpen] = useState(false);
  const [aboutOpen, setAboutOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const registered = useRegisteredCommands();
  usePaletteShortcut(() => setPaletteOpen(true));
  useEditorGestureGuard(); // a trackpad swipe must not navigate back (P16)
  // Snapping to the grid is personal, remembered per board in this browser (P16, backlog §19).
  const snap = useSyncExternalStore(controller.subscribeUi, controller.snapEnabled);
  useEffect(() => {
    if (boardId) controller.setSnapEnabled(readStoredSnap(boardId));
  }, [controller, boardId]);
  const toggleSnap = () => {
    const on = !controller.snapEnabled();
    controller.setSnapEnabled(on);
    if (boardId) writeSnapPref(boardId, on);
  };
  const [arrangeOpen, setArrangeOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [menu, setMenu] = useState<ContextMenuRequest | null>(null);
  const [exporting, setExporting] = useState<{ initial?: ExportTarget; format?: ExportFormat; selection: string[] } | null>(null);
  const minimap = useMinimapOpen();
  const pageSwitcher = <PageSwitcher store={store} nav={nav} onOpenPanel={() => setPagesOpen(true)} />;
  const boardFile = useRef<HTMLInputElement>(null);
  const boardFileAt = useRef<Point>({ x: 0, y: 0 });
  const [fileNotice, setFileNotice] = useState<string | null>(null);
  useEffect(() => controller.subscribeNavigate(() => setMenu(null)), [controller]); // any pan or zoom closes the menu
  useUnfurl(store, links);
  const [failures, setFailures] = useState<ImportFailure[]>([]);
  const [editing, setEditing] = useState<string | null>(null);
  const [editingTitle, setEditingTitle] = useState<string | null>(null);
  const [quick, setQuick] = useState<Point | null>(null);
  const [editingLabel, setEditingLabel] = useState<string | null>(null);
  const [notice, setNotice] = useState<{ key: Notice; n: number } | null>(null);
  const clearNotice = useCallback(() => setNotice(null), []);
  const [commentRequest, setCommentRequest] = useState<CommentRequest | null>(null);
  const [announcement, setAnnouncement] = useState('');
  const viewport = useRef<HTMLDivElement>(null);
  const world = useRef<HTMLDivElement>(null);
  const overlay = useRef<SVGSVGElement>(null);
  const background = useRef<HTMLDivElement>(null);
  const picker = useRef<HTMLInputElement>(null);
  // "Replace image" (P5f): the image whose picture the next picked file replaces.
  const replacer = useRef<HTMLInputElement>(null);
  const replacing = useRef<string | null>(null);
  const camera = useRef<HTMLInputElement>(null);
  const subscribeReadOnly = useCallback((cb: () => void) => store.subscribeReadOnly(cb), [store]);
  const readOnly = useSyncExternalStore(subscribeReadOnly, () => store.readOnly);
  const subscribeList = useCallback((cb: () => void) => store.subscribeList(cb), [store]);
  const empty = useSyncExternalStore(subscribeList, () => store.list().length === 0);

  controller.defaultText = t('editor.defaultText');
  controller.dropLabels = { into: (name) => t('editor.dropInto', { name }), out: (name) => t('editor.dropOut', { name }), leaving: (count, name) => t('editor.dropLeaving', { count, name }), untitled: t('editor.untitledSection') };
  useLayoutEffect(() => {
    controller.handlers = {
      onHelp: () => setHelpOpen(true),
      onPalette: () => setPaletteOpen(true),
      onOpenPageLink: (id) => openPageLink(id),
      onPage: (dir) => {
        const pages = store.pages();
        const next = pages[pages.findIndex((p) => p.id === store.page) + dir];
        if (next) nav.go(next.id);
      },
      onArrange: () => setArrangeOpen(true),
      onContextMenu: (req) => setMenu(req),
      onPickImage: () => picker.current?.click(),
      onEditText: (id) => !store.readOnly && setEditing(id),
      onEditSectionTitle: (id) => !store.readOnly && setEditingTitle(id),
      onEditTableCell: (id, rowId, colId) => !store.readOnly && requestCellEdit(store, id, rowId, colId),
      onEditTask: (id, target) => !store.readOnly && requestTaskEdit(store, id, target),
      onQuickCreate: (req) => setQuick(req.screen),
      onNotice: (key) => setNotice((cur) => ({ key, n: (cur?.n ?? 0) + 1 })),
      onEditConnectorLabel: (id) => !store.readOnly && setEditingLabel(id),
      onComment: (req) => setCommentRequest(req),
      onFocusObject: ({ type, text, index, total }) => {
        const name = t(`a11y.types.${type}`);
        const short = text.length > 80 ? `${text.slice(0, 79)}…` : text;
        setAnnouncement(short ? t('a11y.focusedText', { type: name, text: short, index, total }) : t('a11y.focused', { type: name, index, total }));
      },
    };
    controller.attach({ viewport: viewport.current!, world: world.current!, overlay: overlay.current!, background: background.current ?? undefined });
    return () => controller.detach();
  }, [controller, store]);

  const runImport = async (files: File[], at: Point) => {
    if (!onImport || files.length === 0 || store.readOnly) return;
    const { failed } = await onImport(files, at, (ids) => void controller.adoptIntoSections(ids));
    setFailures(failed);
  };
  // A board file (.osboard, JSON) or a FigJam `.jam`, from the menu or dropped on the board: one path, one notice.
  const runBoardFile = (file: File, at: Point) => {
    if (!importFile || store.readOnly) return;
    importFile(file, at).then(
      (r) => {
        // What came in is shown: a large file would otherwise spread off screen (P12, STATUS 36).
        controller.revealObjects(r.ids);
        setFileNotice(
          [t('boardImport.objects', { count: r.ids.length }), r.skipped ? t('boardImport.skipped', { count: r.skipped }) : '', r.missing ? t('boardImport.missing', { count: r.missing }) : ''].filter(Boolean).join(' · '),
        );
      },
      (err: unknown) => {
        const code = (err as { code?: string }).code ?? '';
        setFileNotice(t(`boardImport.errors.${['invalid', 'corrupt', 'version', 'too_large', 'too_many_pages', 'board_full'].includes(code) ? code : 'failed'}`));
      },
    );
  };
  const onDrop = (e: DragEvent) => {
    e.preventDefault();
    const at = controller.worldAt(e.clientX, e.clientY);
    const boardFile = [...(e.dataTransfer?.files ?? [])].find((f) => /\.(jam|osboard)$/i.test(f.name));
    if (boardFile && importFile) return void runBoardFile(boardFile, at);
    const files = imagesOf(e.dataTransfer?.files);
    if (files.length) return void runImport(files, at);
    // A link dragged from another tab or app becomes a link card or an embed where it is dropped.
    const url = (e.dataTransfer?.getData('text/uri-list') ?? '').split('\n').find((l) => l.trim() && !l.startsWith('#'))?.trim() ?? e.dataTransfer?.getData('text/plain').trim();
    if (url) controller.createLink(url, at);
  };
  const importRef = useRef(runImport);
  importRef.current = runImport;
  useEffect(() => controller.setTextEditing(!!editing), [controller, editing]);
  // Another page: nothing of the old one stays open (P14 Ruling 13).
  useEffect(
    () =>
      store.subscribePage(() => {
        setEditing(null);
        setEditingTitle(null);
        setEditingLabel(null);
        setMenu(null);
      }),
    [store],
  );
  const editingRef = useRef(editing);
  editingRef.current = editing;
  // Virtual keyboard (spec §13): its height feeds `--os-keyboard-inset` (docked format bar) and the edited text stays above it.
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv || !editing) return;
    const root = document.documentElement;
    const inset = () => keyboardInset(vv, window.innerHeight);
    // Only a change of the visible area moves the camera: starting an edit never pans by itself.
    const update = () => {
      root.style.setProperty('--os-keyboard-inset', `${inset()}px`);
      const w = store.graph().world.get(editing);
      // The docked format bar (phones) rests on the keyboard: the text must clear it too.
      const docked = document.querySelector('[data-docked]')?.getBoundingClientRect().height ?? 0;
      if (w) controller.revealBox({ x: w.x, y: w.y, w: w.w, h: w.h }, inset() + docked);
    };
    root.style.setProperty('--os-keyboard-inset', `${inset()}px`);
    vv.addEventListener('resize', update);
    vv.addEventListener('scroll', update);
    return () => {
      vv.removeEventListener('resize', update);
      vv.removeEventListener('scroll', update);
      root.style.removeProperty('--os-keyboard-inset');
    };
  }, [controller, store, editing]);
  useClipboard({ store, controller, importFiles: (files, at) => void importRef.current(files, at), editing: () => !!editingRef.current, canCopyOut });

  const pickBoardFile = (at: Point) => {
    boardFileAt.current = at;
    boardFile.current?.click();
  };
  // Sections in reading order, read when a menu or the palette opens (P5e); going to one fits it in the view. Every
  // page's (P14), prefixed by the page's name once there are several; going to one on another page opens that page.
  const sectionList = () => {
    const pages = store.pages();
    return pages.flatMap((p, i) =>
      sectionsInReadingOrder(store, p.id).map(({ id, name }) => ({
        id,
        name: pages.length > 1 ? `${pageLabel(t, p, i)} › ${name || t('editor.sectionPlaceholder')}` : name,
      })),
    );
  };
  const goToSection = (id: string) => {
    const page = store.pageOf(id);
    if (page && page !== store.page) nav.go(page);
    const w = store.graph().world.get(id);
    if (w) controller.zoomToBox(w);
    controller.focus();
  };
  // Read when the menu opens (live read-only state, what the page registered: rename, history…).
  const mainMenuEntries = () => {
    const reg = registered();
    const page = (id: string) => reg.find((c) => c.id === id)?.run;
    return mainMenu({
      t,
      readOnly: store.readOnly,
      run: (id) => {
        controller.runCommand(id);
        controller.focus();
      },
      registered: reg,
      onRename: page('rename'),
      onDuplicateBoard: page('duplicateBoard'),
      onExport: exporter ? (format) => setExporting({ selection: [...controller.selection], format }) : undefined,
      onImport: importFile && !store.readOnly ? () => pickBoardFile(controller.viewCenter()) : undefined,
      onImportCsv: store.readOnly ? undefined : () => setCsvAt(controller.viewCenter()),
      onImportFigma: importFigma && !store.readOnly ? () => setFigmaAt(controller.viewCenter()) : undefined,
      onSaveTemplate: page('saveTemplate'),
      onSettings: () => setSettingsOpen(true),
      snap: { on: controller.snapEnabled(), toggle: toggleSnap },
      // Phones have no overview to show.
      onToggleMinimap: phone ? undefined : minimap[1],
      onTheme: () => writeThemePreference(document.documentElement.dataset.theme === 'light' ? 'dark' : 'light'),
      onHelp: () => setHelpOpen(true),
      onAbout: () => setAboutOpen(true),
      sections: sectionList(),
      onGoToSection: goToSection,
      onInsertTaskList: () => {
        controller.insertTaskList();
      },
      onInsertPageLink: store.readOnly ? undefined : () => setLinking({}),
      phone,
    });
  };

  // Whether the last key pressed was Tab (cleared by any other key or a pointer): only Tab earns the canvas its focus ring.
  const tabbing = useRef(false);
  const hintId = useId();
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      tabbing.current = e.key === 'Tab';
    };
    const onPointer = () => {
      tabbing.current = false;
    };
    document.addEventListener('keydown', onKey, true);
    document.addEventListener('pointerdown', onPointer, true);
    return () => {
      document.removeEventListener('keydown', onKey, true);
      document.removeEventListener('pointerdown', onPointer, true);
    };
  }, []);

  return (
    <div ref={root} data-editor-root className="relative h-full w-full overflow-hidden bg-bg">
      {/* How to leave the canvas by keyboard (WCAG 2.1.2): Tab on a selected note adds a note, as in FigJam (P12a). */}
      <span id={hintId} className="sr-only">
        {t('editor.keyboardHint')}
      </span>
      <div
        ref={viewport}
        data-layer="viewport"
        role="application"
        aria-label={t('editor.canvas')}
        aria-roledescription={t('editor.roleDescription')}
        aria-describedby={hintId}
        tabIndex={0}
        onDragOver={(e) => e.preventDefault()}
        onDrop={onDrop}
        // The 2 px ring (§14) shows when Tab brings the focus in from another control; not when it comes back from a text edit
        // inside the canvas, nor when a menu or popover closed with Escape gives it back (Chromium calls that focus-visible too).
        onFocus={(e) => {
          if (e.target === e.currentTarget) e.currentTarget.toggleAttribute('data-entered', tabbing.current && !(e.relatedTarget instanceof Node && e.currentTarget.contains(e.relatedTarget)));
        }}
        onBlur={(e) => {
          if (e.target === e.currentTarget) e.currentTarget.removeAttribute('data-entered');
        }}
        className="absolute inset-0 touch-none select-none overflow-hidden outline-none data-[entered]:focus-visible:outline-2 data-[entered]:focus-visible:outline-solid data-[entered]:focus-visible:-outline-offset-2 data-[entered]:focus-visible:outline-focus"
      >
        <div ref={background} data-layer="background" aria-hidden="true" className="pointer-events-none absolute inset-0" />
        <CanvasAuthorsProvider store={store} source={authors}>
        <World ref={world} store={store} media={media} register={controller.register} onObjectRender={onObjectRender} embeds={controller.embeds}>
          {editing && (
            <>
              {/* The static text of the edited object hides under its editor (nanoid ids are selector-safe). */}
              <style>{`[data-id="${editing}"] [data-rich]{visibility:hidden}`}</style>
              <RichTextEditor
                key={editing}
                store={store}
                id={editing}
                awareness={presence?.awareness}
                user={presence ? { name: presence.me.n, color: presence.me.c } : undefined}
                onDone={() => {
                  setEditing(null);
                  controller.focus();
                }}
              />
            </>
          )}
          {editingLabel && (
            <>
              <style>{`[data-connector-label="${editingLabel}"]{visibility:hidden}`}</style>
              <ConnectorLabelEditor
                key={editingLabel}
                store={store}
                id={editingLabel}
                onDone={() => {
                  setEditingLabel(null);
                  controller.focus();
                }}
              />
            </>
          )}
          {editingTitle && (
            <>
              <style>{`[data-section-title="${editingTitle}"]{visibility:hidden}`}</style>
              <SectionTitleEditor
                key={editingTitle}
                store={store}
                id={editingTitle}
                onDone={() => {
                  setEditingTitle(null);
                  controller.focus();
                }}
              />
            </>
          )}
        </World>
        </CanvasAuthorsProvider>
        <Overlay ref={overlay} />
      </div>
      <Announcer message={announcement} />
      {notice && <Toast key={notice.n} message={t(`notices.${notice.key}`)} onDone={clearNotice} />}
      {quick && (
        <QuickCreateMenu
          at={quick}
          onPick={(k) => {
            setQuick(null);
            controller.quickCreate(k);
            if (k === 'shape') controller.focus();
          }}
          onCancel={() => {
            setQuick(null);
            controller.cancelQuickCreate();
            controller.focus();
          }}
        />
      )}
      {presence && <PresenceLayer {...presence} controller={controller} store={store} />}
      {comments && (
        <CommentsLayer controller={controller} store={store} comments={comments} request={commentRequest} onRequestDone={() => setCommentRequest(null)} />
      )}
      {empty && !readOnly && (
        <p aria-hidden="true" className="pointer-events-none absolute left-1/2 top-1/2 z-[5] -translate-x-1/2 -translate-y-1/2 rounded-full bg-surface/90 px-4 py-2 font-sans text-sm text-fg-muted shadow-sm">
          <span className="pointer-coarse:hidden">{t('editor.emptyHint')}</span>
          <span className="hidden pointer-coarse:inline">{t('editor.emptyHintTouch')}</span>
        </p>
      )}
      {/* Phones (portrait or sideways, P15) pinch instead: no overview, no zoom bar. */}
      {!phone && <Minimap store={store} controller={controller} state={minimap} />}
      <InTopBar slot="menu" fallback={null}>
        <MainMenu entries={mainMenuEntries} onActions={() => setPaletteOpen(true)} home={home} />
      </InTopBar>
      {!phone && <ZoomControls controller={controller} onToggleSnap={toggleSnap} />}
      <Toolbar
        controller={controller}
        onPickImage={() => picker.current?.click()}
        onTakePhoto={() => camera.current?.click()}
      />
      {menu && (
        <ContextMenu
          x={menu.x}
          y={menu.y}
          items={menuItemsFor(
            controller.selection.map((id) => store.getObject(id)).filter((o) => !!o),
            { readOnly: store.readOnly, canPaste: !!lastCopied(), canPasteStyle: controller.canPasteStyle(), cell: menu.cell, task: menu.task, canExport: !!exporter, canImportFile: !!importFile, canMoveToPage: store.pages().length > 1, isLocked: (o) => isEffectivelyLocked(store.graph(), (id) => store.getObject(id), o.id) },
          )}
          onAction={(id) => {
            if (id === 'exportCsv') exportTableCsv(controller.selection[0]!);
            else if (id === 'exportSelection') setExporting({ initial: { kind: 'selection', ids: [...controller.selection] }, selection: [...controller.selection] });
            else if (id === 'importFile') pickBoardFile(controller.worldAt(menu.x, menu.y));
            else if (id === 'moveToPage') setMoving([...controller.selection]);
            else controller.runMenuAction(id, menu.cell, menu.task ?? null);
          }}
          onClose={() => {
            setMenu(null);
            controller.focus();
          }}
        />
      )}
      {importFile && (
        <FilePicker
          ref={boardFile}
          accept=".osboard,.jam,.json,application/json,application/zip"
          aria-label={t('menu.importFile')}
          onFiles={([file]) => file && runBoardFile(file, boardFileAt.current)}
        />
      )}
      {fileNotice && <Toast message={fileNotice} onDone={() => setFileNotice(null)} />}
      {figmaAt && importFigma && (
        <FigmaImportDialog
          mode="here"
          api={importFigma.api}
          boardId={importFigma.boardId}
          store={store}
          at={figmaAt}
          ingest={importFigma.ingest}
          onInserted={(ids) => {
            controller.setSelection(ids);
            controller.revealObjects(ids);
          }}
          onClose={() => {
            setFigmaAt(null);
            controller.focus();
          }}
        />
      )}
      {csvAt && (
        <CsvImportDialog
          store={store}
          at={csvAt}
          onClose={() => {
            setCsvAt(null);
            controller.focus();
          }}
          onInserted={(ids, pageId) => {
            if (pageId) {
              nav.go(pageId);
              controller.runCommand('zoomFit');
            } else {
              controller.setSelection(ids);
              controller.revealObjects(ids);
            }
          }}
        />
      )}
      {exporting && exporter && (
        <ExportDialog
          store={store}
          media={media}
          setup={exporter}
          selection={exporting.selection}
          initial={exporting.initial}
          initialFormat={exporting.format}
          onClose={() => {
            setExporting(null);
            controller.focus();
          }}
        />
      )}
      {settingsOpen && (
        <BoardSettings
          store={store}
          snap={{ on: snap, toggle: toggleSnap }}
          onClose={() => {
            setSettingsOpen(false);
            controller.focus();
          }}
        />
      )}
      <InTopBar slot="page" fallback={<div className="pointer-events-auto absolute left-3 top-16 z-20 rounded-lg bg-surface p-1 shadow-md">{pageSwitcher}</div>}>
        {pageSwitcher}
      </InTopBar>
      {linking && (
        <PageLinkDialog
          store={store}
          onPick={pickLinkTarget}
          onClose={() => {
            setLinking(null);
            controller.focus();
          }}
        />
      )}
      {moving && (
        <MoveToPageDialog
          store={store}
          ids={moving}
          onClose={() => {
            setMoving(null);
            controller.focus();
          }}
        />
      )}
      {pagesOpen && (
        <PagesPanel
          store={store}
          nav={nav}
          onClose={() => {
            setPagesOpen(false);
            controller.focus();
          }}
          onDeleted={() => setPageDeleted((n) => n + 1)}
        />
      )}
      {pageDeleted > 0 && (
        <Toast
          key={pageDeleted}
          message={t('pages.deleted')}
          action={{
            label: t('pages.undo'),
            onClick: () => {
              store.undo();
              setPageDeleted(0);
            },
          }}
          onDone={() => setPageDeleted(0)}
        />
      )}
      {arrangeOpen && (
        <ArrangePanel
          controller={controller}
          onClose={() => {
            setArrangeOpen(false);
            controller.focus();
          }}
        />
      )}
      {/* Phones: no properties sheet while typing, it would cover the text kept above the keyboard. */}
      {/* Desktop and tablet: the context bar above the selection (P5f); phones keep the bottom sheet and the author tag. */}
      {!phone && (
        <ContextBar
          controller={controller}
          store={store}
          authors={authors}
          editing={!!(editing || editingTitle || editingLabel)}
          onOpenPageLink={openPageLink}
          onRetargetPageLink={(id) => setLinking({ retarget: id })}
          onExportCsv={exporter ? exportTableCsv : undefined}
          onReplaceImage={
            onImportAsset
              ? (id) => {
                  replacing.current = id;
                  replacer.current?.click();
                }
              : undefined
          }
        />
      )}
      {phone && !readOnly && !editing && <PropertiesPanel controller={controller} store={store} authors={authors} />}
      {phone && authors && !editing && <AuthorTag controller={controller} store={store} source={authors} />}
      <FilePicker ref={picker} accept="image/jpeg,image/png,image/webp,image/gif,image/avif,image/heic,image/heif" multiple onFiles={(files) => void runImport(imagesOf(files), controller.viewCenter())} />
      <FilePicker
        ref={replacer}
        accept="image/jpeg,image/png,image/webp,image/gif,image/avif,image/heic,image/heif"
        data-replace-image=""
        onFiles={(files) => {
          const file = imagesOf(files)[0];
          const id = replacing.current;
          replacing.current = null;
          if (!file || !id || !onImportAsset) return;
          void onImportAsset(file).then(({ asset, failed }) => {
            setFailures(failed);
            // A failed import leaves the image as it was.
            if (asset) store.commands.replaceImage(id, asset.assetId, asset);
          });
        }}
      />
      {/* Touch layouts: the image menu's "Take a photo" opens the rear camera (spec §13). */}
      <FilePicker ref={camera} accept="image/*" capture="environment" onFiles={(files) => void runImport(imagesOf(files), controller.viewCenter())} />
      <BoardLimitNotice store={store} />
      {failures.length > 0 && (
        <div role="alert" className="absolute left-1/2 top-4 z-20 max-w-md -translate-x-1/2 rounded-lg border border-accent bg-surface p-3 font-sans text-sm text-fg shadow-lg">
          <ul className="list-inside list-disc">
            {failures.map((f, i) => (
              <li key={i}>{t(`import.failed.${f.reason}`, { name: f.name })}</li>
            ))}
          </ul>
          <Button variant="ghost" size="sm" onClick={() => setFailures([])} className="mt-2">
            {t('import.dismiss')}
          </Button>
        </div>
      )}
      <CommandPalette
        open={paletteOpen}
        onClose={() => setPaletteOpen(false)}
        commands={
          paletteOpen
            ? uniqueById([
                ...editorCommands({
                  t,
                  controller,
                  readOnly,
                  withComments: !!comments,
                  onExport: exporter ? () => setExporting({ selection: [...controller.selection] }) : undefined,
                  onSettings: () => setSettingsOpen(true),
                  onHelp: () => setHelpOpen(true),
                  sections: sectionList(),
                  onGoToSection: goToSection,
                }),
                ...registered(),
                // Every other action of the main menu (P15c): ⌘K reaches all of them.
                ...menuCommands(mainMenuEntries()),
              ])
            : []
        }
      />
      {helpOpen && (
        <ShortcutsHelp
          onClose={() => {
            setHelpOpen(false);
            controller.focus();
          }}
        />
      )}
      {aboutOpen && (
        <AboutDialog
          onClose={() => {
            setAboutOpen(false);
            controller.focus();
          }}
        />
      )}
    </div>
  );
}
